import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, toJsonFile } from "../../../services/api";
import { displayToast } from "../../../components/app/AppToast";
import type { DictationEvent, DictationHandle } from "../lib/dictationTypes";
import {
  connectRealtimeTranscription,
  isDictationSupported,
} from "../lib/dictationSession";
import {
  MIN_TAKE_BYTES,
  isMicPermissionDenied,
  isRecordedDictationSupported,
  requestMicStream,
  startDictationRecording,
  type DictationRecorderHandle,
} from "@juisci/dictation-recorder";

// ---------------------------------------------------------------------------
// useDictation — voice-dictation state machine for the JuisciGPT composer.
//
// Default path: record the take locally (`@juisci/dictation-recorder`, 16kHz WAV), then on
// stop upload it to our api which calls Gemini 3.5 Transcribe — nothing streams
// while recording, so the composer shows a "transcribing" beat between stop and
// the text landing. Fallback (no AudioWorklet): OpenAI Realtime WebRTC
// (gpt-realtime-whisper), which streams partials live into the composer. Both
// paths land their text through the same fold below.
//
// Composer-agnostic: `start(anchor)` snapshots the input SPLIT at the
// caret, and every transcript update is emitted via `onTranscript(fullText,
// caret)` so the two call sites (in-conversation / landing) wire their own setter
// and put the caret past the insertion. Stopping leaves the text in place — never
// auto-sends, and a following session lands wherever the caret then sits, so the
// user can record / stop / resume as many times as they like.
//
// Transcript folding: a "partial" REPLACES the live hypothesis; a "final" APPENDS
// a finalized utterance to the committed transcript.
//
// useState/useRef/useEffect are used here deliberately: this is a dedicated hook
// (the sanctioned home for state + imperative transport lifecycle), not a business
// component.
// ---------------------------------------------------------------------------

// Hard cap on a single live dictation — auto-stops after this to avoid a stuck
// session holding the mic / billing the provider indefinitely.
const MAX_DICTATION_MS = 5 * 60 * 1000;

export type DictationErrorCause =
  | "unsupported"
  | "permission-denied"
  | "token-mint-failed"
  | "connection-failed"
  | "realtime-error"
  | "transcription-failed";

export type DictationState =
  | { kind: "idle" }
  | { kind: "connecting" }
  | { kind: "listening" }
  // Recorder path only: the take is uploaded and the transcript is in flight.
  | { kind: "transcribing" }
  | { kind: "error"; message: string; cause: DictationErrorCause };

// Where a session's transcript lands: the composer text split at the caret when
// the mic was tapped. Everything spoken is inserted between the two halves (a
// selection is the part that gets replaced — it is in neither half).
export type DictationAnchor = { before: string; after: string };

export type UseDictation = {
  state: DictationState;
  isSupported: boolean;
  // Mic engaged or transcript in flight (connecting/listening/transcribing) —
  // the composer must be locked for this whole window: text typed mid-connect
  // is lost when the first transcript overwrites the snapshot, and text typed
  // mid-transcribe is clobbered when the take's transcript lands at the anchor.
  isActive: boolean;
  start: (anchor: DictationAnchor) => void;
  stop: () => void;
  // Stop AND discard: the composer goes back to the text it held when the
  // session started, as if nothing had been dictated. While transcribing, this
  // abandons the in-flight transcript.
  cancel: () => void;
};

export function useDictation(params: {
  // `caret` is the offset just past the inserted transcript — where the caret
  // belongs once the session ends, and therefore where the NEXT one resumes.
  onTranscript: (text: string, caret: number) => void;
}): UseDictation {
  const { t } = useTranslation();
  const [state, setState] = useState<DictationState>({ kind: "idle" });

  const isSupported = useMemo(() => isDictationSupported(), []);

  // Keep the latest callback reachable from the long-lived async session without
  // capturing a stale closure (assigned every render — no effect needed).
  const onTranscriptRef = useRef(params.onTranscript);
  onTranscriptRef.current = params.onTranscript;

  const handleRef = useRef<DictationHandle | null>(null);
  const recorderRef = useRef<DictationRecorderHandle | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Transcript accumulation: the anchor (pre-existing composer text, split at the
  // caret) around committed (space-joined finalized utterances) + partial (current
  // in-flight hypothesis).
  const anchorRef = useRef<DictationAnchor>({ before: "", after: "" });
  const committedRef = useRef("");
  const partialRef = useRef("");
  // Monotonic id: bumped on every start/stop/unmount so stale async steps from a
  // superseded session no-op instead of clobbering the current one.
  const runIdRef = useRef(0);

  const releaseSession = useCallback(() => {
    if (recorderRef.current !== null) {
      recorderRef.current.abort();
      recorderRef.current = null;
    }
    if (handleRef.current !== null) {
      handleRef.current.stop();
      handleRef.current = null;
    }
    if (streamRef.current !== null) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
  }, []);

  const emit = useCallback(() => {
    const { before, after } = anchorRef.current;
    const committed = committedRef.current;
    const partial = partialRef.current;
    const transcript =
      committed === ""
        ? partial
        : partial === ""
          ? committed
          : `${committed} ${partial}`;
    // Separate the pre-existing composer text from the transcript with a space on
    // each side, so dictating INTO a sentence doesn't glue the spoken words onto
    // the words already there.
    const head =
      transcript !== "" && /\S$/.test(before) ? `${before} ` : before;
    const tail = transcript !== "" && /^\S/.test(after) ? ` ${after}` : after;
    onTranscriptRef.current(
      head + transcript + tail,
      head.length + transcript.length
    );
  }, []);

  // Terminal failure: invalidate the run, release hardware, surface the error in
  // both the state machine (for the button) and a toast (transient feedback).
  const fail = useCallback(
    (cause: DictationErrorCause, message: string) => {
      runIdRef.current += 1;
      releaseSession();
      setState({ kind: "error", message, cause });
      displayToast(message, "error");
    },
    [releaseSession]
  );

  const handleEvent = useCallback(
    (event: DictationEvent) => {
      switch (event.kind) {
        case "partial": {
          partialRef.current = event.text;
          emit();
          return;
        }
        case "final": {
          committedRef.current =
            committedRef.current === ""
              ? event.text
              : `${committedRef.current} ${event.text}`;
          partialRef.current = "";
          emit();
          return;
        }
        case "error": {
          fail("realtime-error", t("Dictation error. Please try again."));
          return;
        }
        default:
          throw new Error(event satisfies never);
      }
    },
    [emit, fail, t]
  );

  // Recorder path, second half: wrap the take, ship it to the api (which calls
  // Gemini 3.5 Transcribe), and land the transcript through the shared fold.
  const finalizeRecording = useCallback(
    async (runId: number, recorder: DictationRecorderHandle) => {
      // Drains the worklet's message queue (the tail of the dictation) before
      // the WAV is built — the mic stream stays held until it returns.
      const blob = await recorder.finish();
      releaseSession();
      if (blob.size < MIN_TAKE_BYTES) {
        setState({ kind: "idle" });
        return;
      }
      setState({ kind: "transcribing" });

      let transcript: string;
      try {
        const response = await apiClient.post(
          "/webapp/private/gpt/transcribe-dictation",
          {
            json: {
              audio: await toJsonFile(
                new File([blob], "dictation.wav", { type: "audio/wav" })
              ),
            },
          }
        );
        transcript = response.body.transcript;
      } catch {
        if (runId === runIdRef.current) {
          fail("transcription-failed", t("Dictation error. Please try again."));
        }
        return;
      }
      if (runId !== runIdRef.current) return;

      if (transcript !== "") {
        handleEvent({ kind: "final", text: transcript });
      }
      setState({ kind: "idle" });
    },
    [releaseSession, fail, handleEvent, t]
  );

  const beginSession = useCallback(
    async (runId: number) => {
      setState({ kind: "connecting" });

      // Capture the mic FIRST — a permission denial is terminal for both
      // providers, so there is nothing to fall back to.
      let stream: MediaStream;
      try {
        stream = await requestMicStream();
      } catch (error) {
        const denied = isMicPermissionDenied(error);
        if (runId === runIdRef.current) {
          fail(
            denied ? "permission-denied" : "connection-failed",
            denied
              ? t("Microphone access denied")
              : t("Dictation connection lost. Please try again.")
          );
        }
        return;
      }
      if (runId !== runIdRef.current) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      streamRef.current = stream;

      // Default transport: the local PCM recorder (Gemini 3.5 Transcribe on
      // stop). A failure to bring the pipeline up falls back to OpenAI below
      // instead of surfacing — nothing has been recorded yet.
      if (isRecordedDictationSupported()) {
        try {
          // Bounded by the session's own auto-stop (MAX_DICTATION_MS), well
          // under the route's 12 MB.
          const recorder = await startDictationRecording(stream, null);
          if (runId !== runIdRef.current) {
            // Superseded while starting: clean up ONLY what this dead run
            // holds locally. The shared refs were released by whoever bumped
            // the run id, and may already belong to a NEWER session — touching
            // them here would kill it mid-listen.
            recorder.abort();
            return;
          }
          recorderRef.current = recorder;
          setState({ kind: "listening" });
          return;
        } catch {
          if (runId !== runIdRef.current) return;
          // Fall through to the OpenAI fallback with the same mic stream.
        }
      }

      // Fallback transport: OpenAI Realtime (gpt-realtime-whisper) over WebRTC,
      // streaming partials live into the composer.
      const callbacks = {
        onEvent: (event: DictationEvent) => {
          if (runId === runIdRef.current) handleEvent(event);
        },
        onClosed: () => {
          if (runId !== runIdRef.current) return;
          fail(
            "connection-failed",
            t("Dictation connection lost. Please try again.")
          );
        },
      };

      let secret;
      try {
        const response = await apiClient.post(
          "/webapp/private/gpt/realtime-transcription-token",
          { json: { language: null } }
        );
        secret = response.body;
      } catch {
        if (runId === runIdRef.current) {
          fail(
            "token-mint-failed",
            t("Couldn't start dictation. Please try again.")
          );
        }
        return;
      }
      if (runId !== runIdRef.current) return;

      let handle: DictationHandle;
      try {
        handle = await connectRealtimeTranscription(
          stream,
          secret.value,
          callbacks
        );
      } catch {
        if (runId === runIdRef.current) {
          fail(
            "connection-failed",
            t("Dictation connection lost. Please try again.")
          );
        }
        return;
      }

      // Superseded while starting: clean up ONLY the locally-held handle — the
      // shared refs may already belong to a newer session (see recorder path).
      if (runId !== runIdRef.current) {
        handle.stop();
        return;
      }
      handleRef.current = handle;
      setState({ kind: "listening" });
    },
    [handleEvent, releaseSession, fail, t]
  );

  const start = useCallback(
    (anchor: DictationAnchor) => {
      if (!isSupported) {
        setState({
          kind: "error",
          message: t("Dictation is not supported here"),
          cause: "unsupported",
        });
        return;
      }
      // Invalidate any in-flight run FIRST (so its async steps no-op), then tear
      // down any live handle/mic before launching — guards a rapid re-start from
      // leaking a stream or letting a superseded run kill the new one.
      runIdRef.current += 1;
      releaseSession();
      const runId = runIdRef.current;
      anchorRef.current = anchor;
      committedRef.current = "";
      partialRef.current = "";
      void beginSession(runId);
    },
    [isSupported, releaseSession, beginSession, t]
  );

  const stop = useCallback(() => {
    // Nothing to stop while transcribing — the session is already over and the
    // transcript is in flight; only cancel() may abandon it.
    if (state.kind === "transcribing") return;
    const recorder = recorderRef.current;
    if (recorder !== null) {
      // Recorder path: stopping starts the transcription. Take the recorder out
      // of the ref BEFORE releasing, so releaseSession doesn't abort the take.
      recorderRef.current = null;
      runIdRef.current += 1;
      void finalizeRecording(runIdRef.current, recorder);
      return;
    }
    runIdRef.current += 1;
    releaseSession();
    setState({ kind: "idle" });
  }, [state.kind, finalizeRecording, releaseSession]);

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    releaseSession();
    // Rewind the composer past everything this session emitted. `emit` only ever
    // wrote the transcript BETWEEN the two anchor halves, so rejoining them undoes
    // it exactly — caret back at the split. (On the recorder path nothing was
    // emitted while listening/transcribing, so this is the identity write.)
    const { before, after } = anchorRef.current;
    onTranscriptRef.current(before + after, before.length);
    committedRef.current = "";
    partialRef.current = "";
    setState({ kind: "idle" });
  }, [releaseSession]);

  const isActive =
    state.kind === "connecting" ||
    state.kind === "listening" ||
    state.kind === "transcribing";

  // Safeguard: never leave the mic open indefinitely — cap a live session at
  // MAX_DICTATION_MS (a forgotten/stuck session would otherwise stream audio and
  // bill the provider until the tab closes). On the recorder path this stops
  // AND transcribes what was said, same as a mic tap.
  useEffect(() => {
    if (state.kind !== "listening") return;
    const timer = setTimeout(() => {
      stop();
    }, MAX_DICTATION_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [state.kind, stop]);

  // Release the mic + connection if the component unmounts mid-session.
  useEffect(() => {
    return () => {
      runIdRef.current += 1;
      releaseSession();
    };
  }, [releaseSession]);

  return { state, isSupported, isActive, start, stop, cancel };
}
